import HelperText from '@mui/material/FormHelperText';
import enNationalities from 'i18n-nationality/langs/en.json';
import React, { useState } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../../../components/ui/select';
import { InlineOtpVerification } from './InlineOtpVerification';
import { PhoneNumberInput } from './PhoneNumberInput';

interface AccountDetailsFormProps {
  setPhoneNumber: React.Dispatch<React.SetStateAction<string | undefined>>;
  phoneNumberError: () => boolean;
  setCountryPhoneCode: React.Dispatch<React.SetStateAction<string | undefined>>;
  setNationality: React.Dispatch<React.SetStateAction<string>>;
  nationality?: string;
  setName: React.Dispatch<React.SetStateAction<string | undefined>>;
  setEmail: React.Dispatch<React.SetStateAction<string | undefined>>;
  name?: string;
  email?: string;
  nameError: string | null;
  emailError: string | null;
  nameTouched: boolean;
  emailTouched: boolean;
  setNameTouched: React.Dispatch<React.SetStateAction<boolean>>;
  setEmailTouched: React.Dispatch<React.SetStateAction<boolean>>;
  initialPhoneNumber?: string;
  initialCountryCode?: string;
  /** Current phone number value (for OTP widget) */
  phoneNumber?: string;
  /** Current country code (digits only, no '+') (for OTP widget) */
  countryCode?: string;
  /** Called with the signed verification token after phone OTP succeeds */
  onPhoneVerified: (token: string) => void;
  /** Called with the signed verification token after email OTP succeeds */
  onEmailVerified: (token: string) => void;
  /** Called when phone verification is reset due to value change */
  onPhoneReset: () => void;
  /** Called when email verification is reset due to value change */
  onEmailReset: () => void;
  /** Pre-verified phone token — restores verified state on remount (checkout prefill or mid-session navigation) */
  preVerifiedPhoneToken?: string;
  /** Pre-verified email token — restores verified state on remount after navigating away */
  preVerifiedEmailToken?: string;
}

export function AccountDetailsForm({
  setPhoneNumber,
  phoneNumberError,
  setCountryPhoneCode,
  setNationality,
  nationality = 'MY',
  setName,
  setEmail,
  name,
  email,
  nameError,
  emailError,
  nameTouched,
  emailTouched,
  setNameTouched,
  setEmailTouched,
  initialPhoneNumber,
  initialCountryCode,
  phoneNumber,
  countryCode,
  onPhoneVerified,
  onEmailVerified,
  onPhoneReset,
  onEmailReset,
  preVerifiedPhoneToken,
  preVerifiedEmailToken
}: Readonly<AccountDetailsFormProps>) {
  // Initialise from pre-verified tokens so state survives navigation away and back
  const [phoneVerified, setPhoneVerified] = useState(!!preVerifiedPhoneToken);
  const [emailVerified, setEmailVerified] = useState(!!preVerifiedEmailToken);
  const [phoneAutoSendSignal, setPhoneAutoSendSignal] = useState(0);
  const [emailAutoSendSignal, setEmailAutoSendSignal] = useState(0);

  const handlePhoneVerified = (token: string) => {
    setPhoneVerified(true);
    onPhoneVerified(token);
  };

  const handleEmailVerified = (token: string) => {
    setEmailVerified(true);
    onEmailVerified(token);
  };

  const handlePhoneReset = () => {
    setPhoneVerified(false);
    onPhoneReset();
  };

  const handleEmailReset = () => {
    setEmailVerified(false);
    onEmailReset();
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    if (!nameTouched) setNameTouched(true);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (!emailTouched) setEmailTouched(true);
  };

  return (
    <div className="mt-8 w-full max-w-md">
      <div className="flex flex-col items-start text-2xl font-bold">
        <span>Enter Account Details</span>
      </div>
      <div className="text-md mt-2 text-gray-500">
        <p>Fill in the required information to proceed further.</p>
      </div>

      <div className="mt-6">
        <label className="block text-sm font-medium text-gray-700">
          Phone Number <span className="text-red-500">*</span>
        </label>
        <PhoneNumberInput
          setCountryPhoneCode={setCountryPhoneCode}
          setPhoneNumber={setPhoneNumber}
          initialPhoneNumber={initialPhoneNumber}
          initialCountryCode={initialCountryCode}
          disabled={phoneVerified}
          onBlur={() => setPhoneAutoSendSignal((prev) => prev + 1)}
        />
        {phoneNumberError() && (
          <HelperText error>Invalid Phone Number</HelperText>
        )}
        <InlineOtpVerification
          type="phone"
          phoneNumber={phoneNumber}
          countryCode={countryCode}
          onVerified={handlePhoneVerified}
          onReset={handlePhoneReset}
          preVerifiedToken={preVerifiedPhoneToken}
          autoSendSignal={phoneAutoSendSignal}
        />
      </div>

      <div className="mt-6">
        <label className="block text-sm font-medium text-gray-700">
          Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          name="user_name"
          value={name || ''}
          onChange={handleNameChange}
          placeholder="Name"
          className={`mt-1 block w-full rounded-md border ${
            nameTouched && nameError ? 'border-red-500' : 'border-gray-300'
          } px-3 py-2 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-sky-500 sm:text-sm`}
        />
        {nameTouched && nameError && (
          <p className="mt-1 text-xs text-red-500">{nameError}</p>
        )}
      </div>

      <div className="mt-6">
        <label className="block text-sm font-medium text-gray-700">
          Email Address <span className="text-red-500">*</span>
        </label>
        <input
          type="email"
          name="user_email"
          value={email || ''}
          onChange={handleEmailChange}
          disabled={emailVerified}
          placeholder="Email Address"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          inputMode="email"
          data-lpignore="true"
          aria-autocomplete="none"
          onBlur={() => setEmailAutoSendSignal((prev) => prev + 1)}
          className={`mt-1 block w-full rounded-md border ${
            emailTouched && emailError ? 'border-red-500' : 'border-gray-300'
          } px-3 py-2 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-sky-500 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500 sm:text-sm`}
        />
        {emailTouched && emailError && (
          <p className="mt-1 text-xs text-red-500">{emailError}</p>
        )}
        <InlineOtpVerification
          type="email"
          email={email}
          onVerified={handleEmailVerified}
          onReset={handleEmailReset}
          preVerifiedToken={preVerifiedEmailToken}
          autoSendSignal={emailAutoSendSignal}
        />
      </div>

      <div className="mt-6">
        <label
          htmlFor="nationality-select"
          className="block text-sm font-medium text-gray-700"
        >
          Nationality <span className="text-red-500">*</span>
        </label>
        <Select value={nationality} onValueChange={setNationality}>
          <SelectTrigger
            id="nationality-select"
            className="focus:ring-none mt-1 border-gray-300 text-gray-800 focus:ring-0"
          >
            <SelectValue placeholder="Nationality" />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(enNationalities.nationalities).map(
              ([code, label]) => (
                <SelectItem key={code} value={code}>
                  {label}
                </SelectItem>
              )
            )}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
